import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ChatSessionList from './ChatSessionList';
import ChatSession from './ChatSession';
import { ChatSession as ChatSessionType, Message } from '@/types/chat';
import { useSessions } from '@/hooks/useSessions';
import { useSessionDetail } from '@/hooks/useSessionDetail';
import { useAPIClient } from '@/hooks/useAPIClient';
import { useToast } from '@/hooks/use-toast';
import { CHATBOT_WELCOME_MESSAGE } from '@/constants/messages';

const ChatContainer = () => {
  const { 
    sessions, 
    isLoading, 
    isError, 
    createSession, 
    deleteSession,
    clearAllSessions,
    isCreating,
    isDeleting,
    isClearingAll,
    refetch
  } = useSessions();
  
  const apiClient = useAPIClient();
  const { toast } = useToast();
  
  const [activeSessionId, setActiveSessionId] = useState<string>('');
  const [chatSidebarCollapsed, setChatSidebarCollapsed] = useState(false);
  const [localSessions, setLocalSessions] = useState<Map<string, ChatSessionType>>(new Map());
  
  // Track if we're actively streaming to prevent overwrites
  const isStreamingRef = useRef<boolean>(false);

  // Get detailed session info for active session
  const { session: sessionDetail } = useSessionDetail(activeSessionId);

  // Define session creation handler first (before useEffects that reference it)
  const handleCreateSession = useCallback(async () => {
    try {
      const newSession = await createSession.mutateAsync();
      setActiveSessionId(newSession.session_id);

      // Create local session representation
      const chatSession: ChatSessionType = {
        id: newSession.session_id,
        title: newSession.title, // Use title from API response
        messages: [{
          id: '1',
          type: 'bot',
          content: CHATBOT_WELCOME_MESSAGE,
          timestamp: new Date(),
        }],
        lastMessage: new Date(),
        isActive: true,
        messageCount: 0, // Mark as unused until first user message
      };

      setLocalSessions(prev => new Map(prev).set(newSession.session_id, chatSession));
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  }, [createSession]);

  // Initialize active session when sessions load
  useEffect(() => {
    if (!isLoading && sessions.length > 0 && !activeSessionId) {
      setActiveSessionId(sessions[0].session_id);
    }
  }, [sessions, isLoading, activeSessionId]);

  // Create initial session if none exist
  useEffect(() => {
    if (!isLoading && !isError && sessions.length === 0 && !isCreating) {
      handleCreateSession();
    }
  }, [sessions, isLoading, isError, isCreating, handleCreateSession]);

  // Update local session when detail loads (but preserve optimistic messages during streaming)
  useEffect(() => {
    if (sessionDetail && activeSessionId) {
      const serverMessages: Message[] = sessionDetail.messages.map((msg, index) => ({
        id: `${msg.timestamp}-${index}`,
        type: msg.role === 'user' ? 'user' : 'bot',
        content: msg.content,
        timestamp: new Date(msg.timestamp + 'Z'),
      }));

      setLocalSessions(prev => {
        const currentSession = prev.get(activeSessionId);

        // If we're actively streaming, always preserve local state
        if (isStreamingRef.current) {
          return prev; // Don't update during streaming
        }

        // If we have a current session, check if we should preserve optimistic messages
        if (currentSession) {
          const hasOptimisticMessages = currentSession.messages.some(m => m.id.includes('user-') || m.id.includes('bot-'));
          const shouldPreserve = currentSession.messages.length > serverMessages.length || hasOptimisticMessages;

          if (shouldPreserve) {
            return new Map(prev).set(activeSessionId, {
              ...currentSession,
              title: sessionDetail.title, // Update title from server
            });
          }
        }

        // Otherwise, update with server data
        const chatSession: ChatSessionType = {
          id: sessionDetail.session_id,
          title: sessionDetail.title,
          messages: serverMessages,
          lastMessage: serverMessages.length > 0
            ? serverMessages[serverMessages.length - 1].timestamp
            : new Date(sessionDetail.created_at + 'Z'),
          isActive: true,
        };

        return new Map(prev).set(activeSessionId, chatSession);
      });
    }
  }, [sessionDetail, activeSessionId]);

  const handleDeleteSession = useCallback(async (sessionId: string) => {
    try {
      await deleteSession.mutateAsync(sessionId);

      // Remove from local sessions
      setLocalSessions(prev => {
        const newMap = new Map(prev);
        newMap.delete(sessionId);
        return newMap;
      });

      // Switch to another session if this was active
      if (activeSessionId === sessionId) {
        const remaining = sessions.filter(s => s.session_id !== sessionId);
        setActiveSessionId(remaining[0]?.session_id || '');
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  }, [deleteSession, activeSessionId, sessions]);

  const handleRenameSession = useCallback(async (sessionId: string, newTitle: string) => {
    try {
      // Update on server first
      await apiClient.sessions.update(sessionId, { title: newTitle });

      // Update local state
      setLocalSessions(prev => {
        const newMap = new Map(prev);
        const session = newMap.get(sessionId);
        if (session) {
          newMap.set(sessionId, { ...session, title: newTitle });
        }
        return newMap;
      });

      // Refresh sessions list to get updated data
      refetch();
    } catch (error) {
      console.error('Failed to rename session:', error);
      toast({
        title: "Umbenennen fehlgeschlagen",
        description: "Bitte erneut versuchen",
        variant: "destructive",
        duration: 5000,
      });
    }
  }, [apiClient, refetch, toast]);

  const handleClearAllSessions = useCallback(async () => {
    try {
      const result = await clearAllSessions.mutateAsync();

      // Set the new session as active
      setActiveSessionId(result.new_session.session_id);

      // Create local session with welcome message
      const newChatSession: ChatSessionType = {
        id: result.new_session.session_id,
        title: result.new_session.title,
        messages: [{
          id: '1',
          type: 'bot',
          content: CHATBOT_WELCOME_MESSAGE,
          timestamp: new Date(),
        }],
        lastMessage: new Date(),
        isActive: true,
        messageCount: 0, // Mark as unused until first user message
      };

      // Clear all sessions and add the new one
      setLocalSessions(new Map([[result.new_session.session_id, newChatSession]]));

    } catch (error) {
      console.error('Failed to clear all sessions:', error);
      // The hook already handles the error toast
    }
  }, [clearAllSessions]);

  const updateSession = (sessionId: string, updates: Partial<ChatSessionType>) => {
    setLocalSessions(prev => {
      const newMap = new Map(prev);
      const session = newMap.get(sessionId);
      if (session) {
        const updatedSession = { ...session, ...updates, lastMessage: new Date() };
        newMap.set(sessionId, updatedSession);
      }
      return newMap;
    });
  };

  const activeLocalSession = localSessions.get(activeSessionId);

  // Convert API sessions to display format (with safety check)
  const displaySessions = useMemo(() => {
    if (!Array.isArray(sessions)) return [];

    return sessions.map(s => {
      const local = localSessions.get(s.session_id);
      const apiMessageCount = s.message_count || 0;

      return {
        id: s.session_id,
        title: local?.title || s.title, // Use title from API response as fallback
        messages: local?.messages || [],
        lastMessage: local?.lastMessage || new Date((s.last_activity || s.created_at) + 'Z'),
        isActive: s.session_id === activeSessionId,
        messageCount: apiMessageCount, // Always use API message count as source of truth
      };
    });
  }, [sessions, localSessions, activeSessionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Chat-Sitzungen werden geladen...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full">
        <Alert className="max-w-md">
          <AlertDescription>
            Chat-Sitzungen konnten nicht geladen werden. Bitte überprüfen Sie Ihre Verbindung und aktualisieren Sie die Seite.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full">
      <div className={`${chatSidebarCollapsed ? 'w-12' : 'w-64'} border-r border-border bg-card transition-all duration-300 ease-in-out flex flex-col`}>
        
        {!chatSidebarCollapsed && (
          <ChatSessionList
            sessions={displaySessions}
            activeSessionId={activeSessionId}
            onSessionSelect={setActiveSessionId}
            onNewSession={handleCreateSession}
            onDeleteSession={handleDeleteSession}
            onRenameSession={handleRenameSession}
            onClearAllSessions={handleClearAllSessions}
            onToggleCollapse={() => setChatSidebarCollapsed(true)}
          />
        )}
        
        {chatSidebarCollapsed && (
          <div className="flex flex-col items-center py-3 gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setChatSidebarCollapsed(false)}
              className="p-2 hover:bg-muted/50"
              title="Seitenleiste erweitern"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCreateSession}
              disabled={isCreating}
              className="p-2 hover:bg-muted/50"
              title="Neuer Chat"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      
      <div className="flex-1 flex flex-col">
        {activeLocalSession ? (
          <ChatSession 
            session={activeLocalSession}
            sessionId={activeSessionId}
            onUpdateSession={(updates) => updateSession(activeSessionId, updates)}
            onStreamingChange={(isStreaming) => { 
              isStreamingRef.current = isStreaming; 
            }}
          />
        ) : activeSessionId ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Sitzung wird geladen...</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>Wählen Sie eine Chat-Sitzung aus oder erstellen Sie eine neue</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatContainer;